import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RequirementStatusCategory } from '../../../models/requirement.model';

/**
 * `clientId` ist ein rein FE-seitiger, stabiler Identifier pro Zeile. Wir
 * brauchen ihn fuer den `@for`-Track-Key: `value` ist zu instabil (aendert sich
 * beim Tippen im Rename-Input -> Angular rebuildet das Input-Element -> Fokus
 * geht verloren), und `originalValue` ist fuer neu angelegte / Orphan-Eintraege
 * `null` (kein eindeutiger Key). `clientId` wird beim Erzeugen der Zeile im
 * Parent gesetzt und bleibt fuer die Lebensdauer der Zeile konstant.
 */
export interface StatusEditorEntry {
  clientId: string;
  value: string;
  category: RequirementStatusCategory;
  originalValue: string | null;
  isNew: boolean;
}

@Component({
  selector: 'app-requirement-status-editor',
  imports: [FormsModule, TranslatePipe, MatIcon, CdkDropList, CdkDrag, CdkDragHandle],
  templateUrl: './requirement-status-editor.component.html',
  styleUrl: './requirement-status-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementStatusEditorComponent {
  readonly entries = input.required<StatusEditorEntry[]>();
  readonly open = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  readonly toggleClicked = output<void>();
  readonly entriesChange = output<StatusEditorEntry[]>();
  readonly deleteRequested = output<StatusEditorEntry>();
  readonly reorderPersisted = output<{
    orderedIds: string[];
    nextEntries: StatusEditorEntry[];
    previousEntries: StatusEditorEntry[];
  }>();

  readonly newValue = signal('');

  readonly canDeleteMore = computed(() => this.entries().length > 1);

  onToggle(): void {
    this.toggleClicked.emit();
  }

  onRename(index: number, next: string): void {
    const updated = this.entries().map((e, i) => (i === index ? { ...e, value: next } : e));
    this.entriesChange.emit(updated);
  }

  onDelete(index: number): void {
    if (!this.canDeleteMore()) return;
    const entry = this.entries()[index];
    this.deleteRequested.emit(entry);
  }

  onDrop(event: CdkDragDrop<StatusEditorEntry[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.moveEntry(event.previousIndex, event.currentIndex);
  }

  onHandleKeydown(index: number, event: KeyboardEvent): void {
    let target: number | null = null;
    if (event.key === 'ArrowUp') target = index - 1;
    else if (event.key === 'ArrowDown') target = index + 1;
    else return;
    event.preventDefault();
    if (target < 0 || target >= this.entries().length) return;
    this.moveEntry(index, target);
  }

  private moveEntry(from: number, to: number): void {
    const previous = this.entries();
    const next = [...previous];
    moveItemInArray(next, from, to);
    // Reorder nur persistieren, wenn alle Eintraege BE-bekannt sind (originalValue
    // wird ausschliesslich beim Load aus dem BE gesetzt). Fuer die Initial-Defaults
    // vor dem ersten Load und fuer neu hinzugefuegte Zeilen (isNew) bleibt die
    // Umsortierung rein lokal.
    if (next.every((e) => e.originalValue !== null)) {
      this.reorderPersisted.emit({
        orderedIds: next.map((e) => e.clientId),
        nextEntries: next,
        previousEntries: previous,
      });
    } else {
      this.entriesChange.emit(next);
    }
  }

  onAdd(): void {
    const value = this.newValue().trim();
    if (!value) return;
    if (this.entries().some((e) => e.value === value)) {
      this.newValue.set('');
      return;
    }
    const next: StatusEditorEntry = {
      clientId: this.freshClientId(),
      value,
      category: 'OPEN',
      originalValue: null,
      isNew: true,
    };
    this.entriesChange.emit([...this.entries(), next]);
    this.newValue.set('');
  }

  onNewValueInput(event: Event): void {
    this.newValue.set((event.target as HTMLInputElement).value);
  }

  onNewValueKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onAdd();
    }
  }

  private freshClientId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
